import { Inject, Injectable, Renderer2, RendererFactory2, DOCUMENT } from '@angular/core';

import { Router } from '@angular/router';
import { environment } from '../environments/environment';
import { NewRequestService } from './pages/new-request/new-request.service';
import { addRequestList } from './Store/request.actions';
import { Store } from '@ngrx/store';
import * as data from './countries.json';
import { BehaviorSubject, Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { CommonUtilityService } from './utility/common-utility.service';
import { AbstractControl, FormArray, FormGroup } from '@angular/forms';

export interface AddressDetails {
  country?: string;
  subdivision?: string;
  county?: string;
  city?: string;
  neighborhood?: string;
  streetNumber?: string;
  streetName?: string;
  postalCode?: string;
  fullAddress?: string; // New field
}

type ZoomForCountries = {
  [country: string]: number;
};

@Injectable({
  providedIn: 'root'
})
export class SharedService {
  renderer: Renderer2 | undefined;
  public errorMessage: string = '';
  public zoomSize: any;
  public countries: any = (data as any).default;
  public totalTax = 0;
  commaSeparatedAmount: any
  states: any[] = [];
  zoomForCountries: any = {
    'United States': 3,
    'Saint Lucia': 9,
    'Canada': 3,
    'Russia': 3,
    'India': 4,
    'Brazil': 3,
    'United Kingdom': 6,
    'Jamaica': 8,
    'The Bahamas': 7,
    'Cayman Islands': 9,
    'Algeria': 4,
    'Barbados': 9,
    'U.S. Virgin Islands': 11,
    'Saint Kitts & Nevis': 9
  }
  recaptchaReady = false;

  countryLogoAvailable = ['jamaica', 'barbados'];
  scriptLoaded$ = new BehaviorSubject<boolean>(false);
  scriptUrl = 'https://app.termly.io/resource-blocker/6e829330-ee66-4cdc-9144-02299d825568?autoBlock=on';
  private progressBarSubject = new BehaviorSubject<boolean>(false);
  progressBar$ = this.progressBarSubject.asObservable();

  private isLoadingSubject = new BehaviorSubject<boolean>(false);
  isLoading$ = this.isLoadingSubject.asObservable();

  constructor(
    private router: Router,
    private requestService: NewRequestService,
    private store: Store,
    @Inject(DOCUMENT) private _document: Document,
    private http: HttpClient,
    rendererFactory: RendererFactory2,
    private commonService: CommonUtilityService
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);
    // Install a global handler for Maps auth failures so we can log and notify the app
    try {
      const win: any = window as any;
      if (!win.gm_authFailure) {
        win.gm_authFailure = () => {
          console.error('[SharedService] Google Maps authentication failure (gm_authFailure)');
          try { this.scriptLoaded$.next(false); } catch (e) {}
          try { this.commonService.showSnackbar('Map failed to authorize. Check API key, referer restrictions and billing in Google Cloud Console.'); } catch (e) {}
        };
      }
    } catch (e) {
      // ignore
    }
  }

  /**
   * Load Google Maps JS API script once. Returns a promise that resolves when
   * the global `google.maps` object is available.
   */
  loadGoogleMapsScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        const win: any = window as any;
        if (win.google && win.google.maps) {
          // already loaded
          resolve();
          return;
        }

        // If a loader script tag already exists, attach listeners
        const existing: HTMLScriptElement | null = this._document.querySelector('script[data-gmaps-loader]');
        if (existing) {
          if ((win.google && win.google.maps)) {
            resolve();
          } else {
            existing.addEventListener('load', () => resolve());
            existing.addEventListener('error', (e) => reject(e));
          }
          return;
        }

        const apiKey = (environment && environment.gmap_key) ? environment.gmap_key : '';
        if (!apiKey) {
          // no key configured — resolve so callers can degrade gracefully
          console.warn('Google Maps API key not found in environment.gmap_key');
          resolve();
          return;
        }

        const script: HTMLScriptElement = this._document.createElement('script');
        script.setAttribute('data-gmaps-loader', 'true');
        const src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places,geometry`;
        script.src = src;
        script.async = true;
        script.defer = true;
        console.info('[SharedService] Injecting Google Maps script', src, 'apiKeyLength=', apiKey ? apiKey.length : 0);
        script.onload = () => {
          console.info('[SharedService] Google Maps script loaded successfully');
          try { this.scriptLoaded$.next(true); } catch (e) {}
          resolve();
        };
        script.onerror = (err) => {
          console.error('[SharedService] Failed to load Google Maps script', err, src);
          try { this.scriptLoaded$.next(false); } catch (e) {}
          try { this.commonService.showSnackbar('Map failed to load. See console for details (API key, network, or Google Cloud settings).'); } catch (e) {}
          reject(err);
        };
        this._document.head.appendChild(script);
      } catch (err) {
        reject(err);
      }
    });
  }

  setProgressBarState(state: boolean) {
    this.progressBarSubject.next(state);
  }

  setIsLoadingState(state: boolean) {
    this.isLoadingSubject.next(state);
  }

  getZoomForCountry(country: any) {
    if (this.zoomForCountries && this.zoomForCountries[country]) {
      return this.zoomForCountries[country];
    } else {
      return 6;
    }
  }

  public getCountryFromcode = function (country: string) {
    let name = "";
    if (country) {
      let i = this.countries.findIndex(x => x.alpha2.toLowerCase() == country.toLowerCase());
      if (i >= 0) {
        name = this.countries[i].name;
      } else {
        name = country;
      }
    }

    return name;
  }

  performSearch() {
    this.setIsLoadingState(true);
    this.store.dispatch(addRequestList({ requests: [] }));
    const id = localStorage.getItem('brokerId');
    let search_id = localStorage.getItem('search_id');
    const params = JSON.parse(localStorage.getItem('search_params'));
    const page :any = localStorage.getItem('search_page')
    const pageSize :any = localStorage.getItem('search_pageSize')
    if (search_id) {
      params['id'] = search_id;
    }
    this.requestService.searchBrokerRequest(id, params, Number(page)+1, pageSize).subscribe({
      next: (response: any[]) => {
        this.store.dispatch(addRequestList({ requests: response }));
        this.setIsLoadingState(false);
      },
      error: (error: any) => {
        this.store.dispatch(addRequestList({ requests: [] }));
        this.setIsLoadingState(false);
      }
    })
  }

  checkMatching(request: any, structure: any) {
    return (request == null && structure == null) || request.toString() === structure.toString();
  }

  makeNBI(data: any, status: any) {
    if (!data?.insurable_interest?.length) return { data: [] };
  
    const loc_obj = data.insurable_interest.reduce(
      (acc: any, curr: any) => {

        // skip if calculations exist
        if (curr?.Calculations != null) return acc;
  
        /* -------------------- Push Valid Item -------------------- */
        acc.data.push({
          ...curr,
          locations: [
            {
              insurableInterestLocationLat: curr.insurableInterestLocationLat,
              insurableInterestLocationLng: curr.insurableInterestLocationLng,
              id: curr.id
            }
          ]
        });

        return acc;
      },
      { data: [] }
    );

    return loc_obj;
  }

  updateAmount(value: string | number, i?: any): string {
    let cleanValue: string;
    if (value) {
      if (typeof value === 'number') {
        cleanValue = value.toString();
      } else {
        cleanValue = value;
      }
      cleanValue = cleanValue.replace(/[^0-9]/g, '');
      return this.addCommas(cleanValue);
    }
    return '';
  }

  addCommas(value: any): string {
    if (value === null || value === undefined) return '';

    const str = String(value); // ensure it's a string
    return str.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  getIntegerPayout(value) {
    return value ? Math.round(value * 100) : value;
  }

  getFloatPayout(value) {
    return value ? (value == 100 ? 1 : (value / 100)) : value;
  }

  getOriginalPayout(value) {
    return value ? (value === 1 ? 100 : value * 100) : value;
  }

  loadDistricts(): Observable<any[]> {
    return new Observable(observer => {
      this.http.get('assets/districts.csv', { responseType: 'text' })
        .subscribe(
          (data: string) => {
            let csvToRowArray = data.split("\n");

            if (csvToRowArray.length < 2) {
              observer.error('CSV data is empty or malformed');
              return;
            }

            const disttricts = [];
            for (let index = 1; index < csvToRowArray.length; index++) {
              let row = csvToRowArray[index].split(",");
              if (row.length < 9) continue;

              let rowData = {
                id: row[0]?.trim(),
                name: row[1]?.trim(),
                country_id: row[2]?.trim(),
                country_code: row[3]?.trim(),
                country_name: row[4]?.trim(),
                state_code: row[5]?.trim(),
                type: row[6]?.trim(),
                latitude: row[7]?.trim(),
                longitude: row[8]?.trim()
              };
              disttricts.push(rowData);
            }

            observer.next(disttricts);
            observer.complete();
          },
          error => {
            observer.error('Error loading CSV: ' + error.message);
          }
        );
    });
  }

  loadStates(): Observable<any[]> {
    return new Observable(observer => {
      this.http.get('assets/states.csv', { responseType: 'text' })
        .subscribe(
          (data: string) => {
            let csvToRowArray = data.split("\n");

            if (csvToRowArray.length < 2) {
              observer.error('CSV data is empty or malformed');
              return;
            }

            const states = [];
            for (let index = 1; index < csvToRowArray.length; index++) {
              let row = csvToRowArray[index].split(",");
              if (row.length < 9) continue;

              let rowData = {
                id: row[0]?.trim(),
                name: row[1]?.trim(),
                country_id: row[2]?.trim(),
                country_code: row[3]?.trim(),
                country_name: row[4]?.trim(),
                state_code: row[5]?.trim(),
                type: row[6]?.trim(),
                latitude: row[7]?.trim(),
                longitude: row[8]?.trim()
              };
              states.push(rowData);
            }

            observer.next(states);
            observer.complete();
          },
          error => {
            observer.error('Error loading CSV: ' + error.message);
          }
        );
    });
  }

  restrictKeys(event: KeyboardEvent): void {
    const allowedKeys = [
      'Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete'
    ];

    // Allow control keys
    if (allowedKeys.includes(event.key)) return;

    // Allow numbers only (0-9)
    if (/^[0-9]$/.test(event.key)) return;

    // Block anything else
    event.preventDefault();
  }

  numberBetweenZeroToHundred(event: any): void {
    const value = Number(event.target.value);

    if (value < 0) {
      event.target.value = 0;
    } else if (value > 100) {
      event.target.value = 100;
    }
  }

  numberOnly(event: KeyboardEvent, type: string = ''): boolean {
    const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'];

    if (allowedKeys.includes(event.key)) {
      return true; // Allow essential keys
    }

    let pattern: RegExp;

    switch (type.toLowerCase()) {
      case 'date':
        pattern = /^[0-9\/-]$/; // Allow numbers and '/' or '-' for date input
        break;
      case 'number':
        pattern = /^[0-9]$/; // Allow only numbers
        break;
      default:
        pattern = /^[0-9]$/; // Default to numeric input
    }

    if (!pattern.test(event.key)) {
      event.preventDefault();
      return false;
    }

    return true;
  }


  numberWithMinus(event: KeyboardEvent, value: string = ''): boolean {
    const allowedKeys = ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete'];

    // Allow control/navigation keys
    if (allowedKeys.includes(event.key)) return true;

    // Prevent emoji input
    const isEmoji = /\p{Extended_Pictographic}/u.test(event.key);
    if (isEmoji) {
      event.preventDefault();
      return false;
    }

    // Allow numbers (0-9)
    if (/^\d$/.test(event.key)) return true;

    // Allow '-' only at the beginning and not repeated
    if (event.key === '-') {
      const input = event.target as HTMLInputElement;
      const cursorPos = input.selectionStart ?? 0;

      if (!value.includes('-') && cursorPos === 0) return true;
      return false;
    }

    // Allow '.' only if not already present and not first character
    if (event.key === '.' && !value.includes('.') && value !== '-' && value.length > 0) {
      return true;
    }

    // Otherwise, block input
    return false;
  }

  alphanumericOnly(event: KeyboardEvent, name = ''): boolean {
    const allowedKeys = ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete'];

    if (allowedKeys.includes(event.key)) return true;

    const inputElement = event.target as HTMLInputElement;
    const currentValue = inputElement.value;

    const isEmoji = /\p{Extended_Pictographic}/u.test(event.key);
    if (isEmoji) {
      event.preventDefault();
      return false;
    }

    if (event.key === ' ') {
      // Prevent space at the start or consecutive spaces
      if (currentValue.length === 0 || currentValue.endsWith(' ')) {
        event.preventDefault();
        return false;
      }
      return true;
    }

    const isAllowedChar = /^[a-zA-Z0-9\-.,@_!#$%&*()+`~='"/]$/.test(event.key);

    if (!isAllowedChar) {
      event.preventDefault();
      return false;
    }

    return true;
  }

  removeEmojis(value: string, name = ''): string {
    if (!value || typeof value !== 'string') return '';

    // Remove emojis
    value = value.replace(/\p{Extended_Pictographic}+/gu, '');

    if (name === 'lat' || name === 'lng') {
      // Allow digits, one optional leading minus, and one dot
      value = value.replace(/[^0-9.\-]/g, '');

      // Match optional leading minus, followed by digits, optional decimal part
      const match = value.match(/^-$|^-?\d+(\.\d*)?$/);
      return match ? match[0] : '';
    }

    // For general input: keep all characters except emojis (already removed above)
    // Optional: collapse multiple spaces into one
    return value.replace(/ {2,}/g, ' ');
  }


  blockSpaceAndSpecialKeys(event: KeyboardEvent) {
    const forbiddenKeys = [' '];

    if (forbiddenKeys.includes(event.key)) {
      event.stopPropagation();
    }
  }

  isValueZeroLimit(iiVal: any[], page = ''): boolean {
    const isMainPage = page === '';

    for (let i = 0; i < iiVal.length; i++) {
      const item = iiVal[i];
      const indexText = ` at Insurable Interest ${i + 1}.`;

      const checks = isMainPage
        ? [
          { value: item.insuredRiskFactorValue, label: 'Exposure Value' },
          { value: item.propertyInsuredValue, label: 'Property Insured Value' }
        ]
        : [
          { value: item.reinsuranceExposureValue, label: 'Exposure Value' },
        ];

      for (const { value, label } of checks) {
        if (value !== undefined && value !== null && value !== '') {
          if (Number(value) === 0) {
            const suffix = isMainPage ? indexText : ' at Insurer.';
            this.commonService.showSnackbar(`${label} cannot be 0${suffix}`);
            return false;
          }
        }
      }
    }
    return true;
  }

  isPremiumZero(value: any): boolean {
    if (+value === 0 && value) {
      this.commonService.showSnackbar(`Premium Budget cannot be 0 at Contract Type.`);
      return false;
    }
    return true;
  }

  hasOnlySpacesOrSpecialChars(
    formGroup: FormGroup | FormArray,
    formatFieldLabel?: (field: string) => string,
    parentKey: string = ''
  ): boolean {
  
    const controls = this.getControls(formGroup);
  
    for (const { key, control } of controls) {
  
      const fullKey = parentKey ? `${parentKey}.${key}` : key;
  
      if (control instanceof FormGroup || control instanceof FormArray) {
        if (!this.hasOnlySpacesOrSpecialChars(control, formatFieldLabel, fullKey)) {
          return false;
        }
        continue;
      }
  
      const value = control?.value;
  
      if (value == null || value === '') continue;
  
      if (typeof value === 'string') {
        if (!this.validateStringField(control, value, key, fullKey, formatFieldLabel)) {
          return false;
        }
      }
  
    }
  
    return true;
  }

  private getControls(
    formGroup: FormGroup | FormArray
  ): { key: string; control: AbstractControl }[] {
  
    if (formGroup instanceof FormGroup) {
      return Object.keys(formGroup.controls).map(key => ({
        key,
        control: formGroup.controls[key]
      }));
    }
  
    return formGroup.controls.map((control, index) => ({
      key: `[${index}]`,
      control
    }));
  }

  private validateStringField(
    control: AbstractControl | null,
    value: string,
    key: string,
    fullKey: string,
    formatFieldLabel?: (field: string) => string
  ): boolean {
  
    const trimmed = value.trim();
  
    let label = formatFieldLabel ? formatFieldLabel(fullKey) : fullKey;
  
    label = label.replaceAll(/Street\s*No/gi, 'Address line');
    label = label.replaceAll(/Org/gi, 'Organization');
  
    if (trimmed === '') {
      this.showError(control, `${label} must not contain only spaces.`);
      return false;
    }
  
    if (/^[^a-zA-Z0-9]+$/.test(trimmed)) {
      this.showError(control, `${label} must not contain only special characters.`);
      return false;
    }
  
    const isNameField = /(org|name|firstname|familyname|city|company\s*name)/i.test(key);
  
    if (isNameField && /^\d+$/.test(trimmed)) {
      this.showError(control, `${label} must not contain only numbers.`);
      return false;
    }
  
    if (isNameField && !/[a-zA-Z]/.test(trimmed)) {
      this.showError(control, `${label} must contain at least one alphabet character.`);
      return false;
    }
  
    return true;
  }

  private showError(control: AbstractControl | null, message: string) {
    this.commonService.showSnackbar(message);
    control?.markAsTouched();
  }

  getLocationFromPlaceId(placeId: string): Promise<{ lat: number; lng: number } | null> {
    const apiKey = environment.gmap_key;
    const url = `https://maps.googleapis.com/maps/api/place/details/json?place_id=${placeId}&key=${apiKey}`;

    return fetch(url)
      .then(res => res.json())
      .then(data => {
        const result = data.result;
        if (!result || !result.geometry?.location) return null;
        const { lat, lng } = result.geometry.location;
        return { lat, lng };
      })
      .catch(err => {
        console.error('Place ID fetch error:', err);
        return null;
      });
  }

  geocodePlaceName(query: string): Promise<{ lat: number; lng: number } | null> {
    const apiKey = environment.gmap_key; // 🔒 Secure this in environment
    const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(query)}&key=${apiKey}`;

    return fetch(url)
      .then(res => res.json())
      .then(data => {
        const result = data.results?.[0];
        if (!result) return null;
        const { lat, lng } = result.geometry.location;
        return { lat, lng };
      })
      .catch(err => {
        return null;
      });
  }
  reverseGeocode(lat: number, lng: number): Promise<{
    country: string;
    state?: string;
    county?: string;
    city?: string;
    postalCode?: string;
    streetNumber?: string;
    route?: string;
    formattedAddress?: string;
  } | null> {
    const apiKey = environment.gmap_key;
    const url = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${apiKey}`;

    return fetch(url)
      .then(res => res.json())
      .then(data => {
        const result = data.results?.[0];
        if (!result) return null;

        let country = '';
        let state = '';
        let county = '';
        let city = '';
        let postalCode = '';
        let streetNumber = '';
        let route = '';

        for (const component of result.address_components) {
          const types = component.types;
          if (types.includes('country')) country = component.short_name;
          if (types.includes('administrative_area_level_1')) state = component.long_name;
          if (types.includes('administrative_area_level_2')) county = component.long_name;
          if (types.includes('locality') || types.includes('sublocality') || types.includes('postal_town')) city = component.long_name;
          if (types.includes('postal_code')) postalCode = component.long_name;
          if (types.includes('street_number')) streetNumber = component.long_name;
          if (types.includes('route')) route = component.long_name;
        }

        return { country, state, county, city, postalCode, streetNumber, route, formattedAddress: result.formatted_address || '' };
      })
      .catch(err => {
        console.error('Reverse geocoding error:', err);
        return null;
      });
  }

  onMapUrlInput(inputRef: HTMLInputElement) {
    const inputValue = inputRef.value || '';
    if (inputValue !== "") {

      const parts = inputValue.split('https://www.google.com/maps').filter(Boolean);

      if (parts.length > 0) {
        const lastUrl = 'https://www.google.com/maps' + parts[parts.length - 1].trim();

        inputRef.value = lastUrl;
      } else {
        inputRef.value = '';
        this.commonService.showSnackbar('Please enter a valid Google Maps URL.');
      }
    }
  }

  removeValueFromLocalStorage() {
    localStorage.removeItem('adapterParams');
    localStorage.removeItem('calculatedValues');
    localStorage.removeItem('categories');
    localStorage.removeItem('type');
    localStorage.removeItem('thresholdStart');
    localStorage.removeItem('thresholdEnd');
    localStorage.removeItem('premiumGross');
    localStorage.removeItem('dataSource');
    localStorage.removeItem('premiumCurrencySymbol')
    localStorage.removeItem('myFormData')
    localStorage.removeItem('name')
    localStorage.removeItem('lastValue')
    localStorage.removeItem('isSimpsonSelected')
  }

  enterValueMin10Max100(event: any) {
    const input = event.target;
    const rawValue = input.value;

    const value = parseInt(rawValue, 10);
    const min = 10;
    const max = 100;

    if (isNaN(value)) {
      // Optionally reset or ignore empty/invalid input
      input.value = min; // or "", or keep as is
      event.target.dispatchEvent(new Event('input'));
      return;
    }

    if (value < min) {
      input.value = min;
      event.target.dispatchEvent(new Event('input'));
    } else if (value > max) {
      input.value = max;
      event.target.dispatchEvent(new Event('input'));
    }
  }

  blockDotAndMinus(event: KeyboardEvent) {
    const invalidChars = ['-', '.'];

    if (invalidChars.includes(event.key)) {
      event.preventDefault();
    }
  }

  removeEnclosingQuotes(str) {
    if (
      (str?.startsWith('"') && str?.endsWith('"')) ||
      (str?.startsWith("'") && str?.endsWith("'"))
    ) {
      return str?.slice(1, -1);
    }
    return str;
  }


  loadRecaptcha(): Promise<void> {
    return new Promise((resolve, reject) => {
      const scriptId = 'recaptcha-script';

      if (this.recaptchaReady) {
        resolve();
        return;
      }

      const existingScript = document.getElementById(scriptId);

      if (existingScript) {
        this.recaptchaReady = true;
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.id = scriptId;
      script.src = `https://www.google.com/recaptcha/api.js?render=${environment.captcha_siteKey}`;
      script.async = true;
      script.defer = true;

      script.onload = () => {
        this.recaptchaReady = true;
        resolve();
      };

      script.onerror = () => {
        reject(new Error('Failed to load reCAPTCHA script.'));
      };

      document.body.appendChild(script);
    });
  }

  execute(action: string): Promise<string> {
    return new Promise((resolve, reject) => {

      const grecaptcha = (globalThis as any).grecaptcha;

      if (!grecaptcha) {
        reject(new Error('grecaptcha not loaded'));
        return;
      }

      grecaptcha.ready(() => {
        grecaptcha.execute(environment.captcha_siteKey, { action })
          .then((token: string) => resolve(token))
          .catch((err: unknown) => reject(err));
      });
    });
  }
}